import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { List, X, CaretDown, User } from "@phosphor-icons/react";
import QuickRechargeButton from "@/components/QuickRechargeButton";

const formatEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(Number(n) || 0);

export default function Navbar() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const [balancesOpen, setBalancesOpen] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const balancesRef = useRef(null);
    const panelRef = useRef(null);

    const isAuth = user && user !== false;

    async function handleLogout() {
        await logout();
        nav("/");
    }

    // Cerrar dropdowns al clicar fuera
    useEffect(() => {
        function onClickOutside(e) {
            if (balancesRef.current && !balancesRef.current.contains(e.target)) setBalancesOpen(false);
            if (panelRef.current && !panelRef.current.contains(e.target)) setPanelOpen(false);
        }
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    // Cargar resumen de créditos al abrir el dropdown del panel
    useEffect(() => {
        if (!panelOpen || !isAuth) return;
        setSummaryLoading(true);
        api.get("/user/credits-summary")
            .then(({ data }) => setSummary(data))
            .catch(() => setSummary(null))
            .finally(() => setSummaryLoading(false));
    }, [panelOpen, isAuth]);

    const MainLinks = ({ onClick, mobile = false }) => (
        <>
            <Link
                to="/"
                onClick={onClick}
                className={`text-sm transition-colors ${loc.pathname === "/" ? "text-[var(--gf-gold)] font-semibold" : "hover:text-[var(--gf-gold)]"}`}
                data-testid="nav-home"
            >
                Inicio
            </Link>

            {/* GESTIÓN DE BALANCES · Dictamen Pre-Bancario / Informe Financiero / Informe Master */}
            {mobile ? (
                <>
                    <span className="text-sm font-semibold text-[var(--gf-navy)]" data-testid="nav-balances">Gestión de balances</span>
                    <Link to="/maquina-verdad" onClick={onClick} className="text-sm hover:text-[var(--gf-gold)] pl-4" data-testid="nav-dictamen-mobile">
                        └ Dictamen Pre-Bancario
                    </Link>
                    <Link to="/analisis/nuevo" onClick={onClick} className="text-sm hover:text-[var(--gf-gold)] pl-4" data-testid="nav-informe-mobile">
                        └ Informe Financiero
                    </Link>
                    <Link to="/dashboard?master=1" onClick={onClick} className="text-sm hover:text-[var(--gf-gold)] pl-4" data-testid="nav-master-mobile">
                        └ Informe Master
                    </Link>
                    {isAuth && (
                        <Link to="/comparativa" onClick={onClick} className="text-sm hover:text-[var(--gf-gold)] pl-4" data-testid="nav-compare-mobile">
                            └ Comparar balances
                        </Link>
                    )}
                </>
            ) : (
                <div className="relative" ref={balancesRef}>
                    <button
                        onClick={() => { setBalancesOpen(!balancesOpen); setPanelOpen(false); }}
                        className={`text-sm flex items-center gap-1 transition-colors ${loc.pathname.startsWith("/maquina-verdad") || loc.pathname.startsWith("/analisis") || loc.pathname.startsWith("/comparativa") ? "text-[var(--gf-gold)] font-semibold" : "hover:text-[var(--gf-gold)]"}`}
                        data-testid="nav-balances"
                    >
                        Gestión de balances <CaretDown size={12} weight="bold" className={balancesOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                    </button>
                    {balancesOpen && (
                        <div className="absolute top-full left-0 mt-2 min-w-[260px] bg-white border border-[var(--gf-border)] shadow-lg py-2 z-50" data-testid="nav-balances-dropdown">
                            <Link to="/maquina-verdad" onClick={() => setBalancesOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-[var(--gf-subtle)]" data-testid="nav-dictamen">
                                <div className="font-semibold text-[var(--gf-navy)]">Dictamen Pre-Bancario</div>
                                <div className="text-[11px] text-[var(--gf-text-muted)]">Test de 9 ratios bancarios · 14 créditos</div>
                            </Link>
                            <Link to="/analisis/nuevo" onClick={() => setBalancesOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-[var(--gf-subtle)]" data-testid="nav-informe">
                                <div className="font-semibold text-[var(--gf-navy)]">Informe Financiero</div>
                                <div className="text-[11px] text-[var(--gf-text-muted)]">Balance Básico con IA · 24 créditos</div>
                            </Link>
                            <Link to="/dashboard?master=1" onClick={() => setBalancesOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-[var(--gf-subtle)]" data-testid="nav-master">
                                <div className="font-semibold text-[var(--gf-navy)]">Informe Master</div>
                                <div className="text-[11px] text-[var(--gf-text-muted)]">Se genera sobre un informe previo · 44 créditos</div>
                            </Link>
                            {isAuth && (
                                <Link to="/comparativa" onClick={() => setBalancesOpen(false)} className="block px-4 py-2 text-sm border-t border-[var(--gf-border)] mt-1 pt-2 hover:bg-[var(--gf-subtle)]" data-testid="nav-compare">
                                    Comparar balances
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* GESTIÓN OCR Y FACTURAS */}
            {isAuth && (
                <Link
                    to="/portal-asesor"
                    onClick={onClick}
                    className={`text-sm transition-colors ${loc.pathname.startsWith("/portal-asesor") ? "text-[var(--gf-gold)] font-semibold" : "hover:text-[var(--gf-gold)]"}`}
                    data-testid="nav-ocr"
                >
                    Gestión OCR y Facturas
                </Link>
            )}

            {/* FACTURACIÓN VERIFACTU */}
            {isAuth && (
                <Link
                    to="/verifactu"
                    onClick={onClick}
                    className={`text-sm transition-colors ${loc.pathname.startsWith("/verifactu") ? "text-[var(--gf-gold)] font-semibold" : "hover:text-[var(--gf-gold)]"}`}
                    data-testid="nav-verifactu"
                >
                    Facturación Verifactu
                </Link>
            )}

            {user?.role === "admin" && (
                <Link to="/admin" onClick={onClick} className="text-sm text-[var(--gf-gold)] font-semibold hover:underline" data-testid="nav-admin">
                    Admin
                </Link>
            )}
        </>
    );

    const PanelDropdown = () => (
        <div className="relative" ref={panelRef}>
            <button
                onClick={() => { setPanelOpen(!panelOpen); setBalancesOpen(false); }}
                className="flex items-center gap-2 text-sm px-3 py-2 border border-[var(--gf-border)] hover:border-[var(--gf-gold)] transition-colors"
                data-testid="nav-panel-toggle"
            >
                <User size={14} weight="light" />
                <span className="hidden xl:inline">Panel de control</span>
                <span className="xl:hidden">Panel</span>
                <CaretDown size={12} weight="bold" className={panelOpen ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            {panelOpen && (
                <div className="absolute top-full right-0 mt-2 min-w-[320px] bg-white border border-[var(--gf-border)] shadow-lg py-2 z-50" data-testid="nav-panel-dropdown">
                    <div className="px-4 py-2 border-b border-[var(--gf-border)]">
                        <div className="text-[10px] uppercase tracking-widest text-[var(--gf-text-muted)]">Usuario</div>
                        <div className="text-sm font-semibold text-[var(--gf-navy)] truncate" data-testid="nav-user-email">{user?.email}</div>
                        {user?.tipo_cliente && (
                            <div className="text-[10px] text-[var(--gf-text-secondary)] uppercase mt-1">{user.tipo_cliente}</div>
                        )}
                    </div>

                    {/* Resumen de compras, consumo y saldo */}
                    <CreditsSummary summary={summary} loading={summaryLoading} />

                    <Link to="/perfil" onClick={() => setPanelOpen(false)} className="block px-4 py-2 text-sm hover:bg-[var(--gf-subtle)]" data-testid="nav-profile">
                        Editar mi perfil
                    </Link>
                    <Link to="/dashboard" onClick={() => setPanelOpen(false)} className="block px-4 py-2 text-sm hover:bg-[var(--gf-subtle)]" data-testid="nav-dashboard">
                        Panel · Consumos y planes
                    </Link>
                    <Link to="/comparativa" onClick={() => setPanelOpen(false)} className="block px-4 py-2 text-sm hover:bg-[var(--gf-subtle)]">
                        Comparar balances
                    </Link>
                    <Link to="/pagos" onClick={() => setPanelOpen(false)} className="block px-4 py-2 text-sm hover:bg-[var(--gf-subtle)]" data-testid="nav-payments">
                        Planes y créditos
                    </Link>
                    <Link to="/mis-compras" onClick={() => setPanelOpen(false)} className="block px-4 py-2 text-sm hover:bg-[var(--gf-subtle)]" data-testid="nav-billing">
                        Mis compras y facturas
                    </Link>
                    <div className="border-t border-[var(--gf-border)] mt-1 pt-1">
                        <button onClick={() => { setPanelOpen(false); handleLogout(); }} className="w-full text-left px-4 py-2 text-sm text-[var(--gf-danger)] hover:bg-[var(--gf-subtle)]" data-testid="nav-logout">
                            Cerrar sesión
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[var(--gf-border)]" data-testid="main-nav">
            <div className="max-w-7xl mx-auto px-4 md:px-8 h-24 md:h-28 flex items-center justify-between">
                <Link to="/" className="flex items-center" data-testid="brand-logo">
                    <img src="/logo.png" alt="Gestión Fiscal 347" className="h-28 md:h-36 w-auto object-contain -my-6" />
                </Link>

                <div className="hidden lg:flex items-center gap-7">
                    <MainLinks />
                </div>

                <div className="hidden lg:flex items-center gap-3">
                    {!isAuth ? (
                        <>
                            <Link to="/login" className="text-sm hover:text-[var(--gf-gold)]" data-testid="nav-login">
                                Acceder
                            </Link>
                            <Link to="/registro" className="btn-primary text-sm px-5 py-2" data-testid="nav-register">
                                Crear cuenta
                            </Link>
                        </>
                    ) : (
                        <PanelDropdown />
                    )}
                </div>

                <button className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Menú" data-testid="nav-mobile-toggle">
                    {open ? <X size={24} /> : <List size={24} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-[var(--gf-border)] bg-white px-6 py-4 flex flex-col gap-4" data-testid="nav-mobile-menu">
                    <MainLinks onClick={() => setOpen(false)} mobile />
                    {isAuth && (
                        <>
                            <div className="border-t border-[var(--gf-border)] pt-3 text-[10px] uppercase tracking-widest text-[var(--gf-text-muted)]">
                                Panel de control
                            </div>
                            <div className="text-xs text-[var(--gf-text-secondary)] truncate">{user?.email}</div>
                            <Link to="/perfil" onClick={() => setOpen(false)} className="text-sm hover:text-[var(--gf-gold)]">Editar mi perfil</Link>
                            <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm hover:text-[var(--gf-gold)]">Panel · Consumos y planes</Link>
                            <Link to="/pagos" onClick={() => setOpen(false)} className="text-sm hover:text-[var(--gf-gold)]">Planes y créditos</Link>
                            <Link to="/facturacion" onClick={() => setOpen(false)} className="text-sm hover:text-[var(--gf-gold)]">Facturas</Link>
                        </>
                    )}
                    {!isAuth ? (
                        <div className="flex gap-3 pt-2">
                            <Link to="/login" className="btn-outline text-sm flex-1 justify-center" onClick={() => setOpen(false)}>Acceder</Link>
                            <Link to="/registro" className="btn-primary text-sm flex-1 justify-center" onClick={() => setOpen(false)}>Registrarse</Link>
                        </div>
                    ) : (
                        <button onClick={() => { setOpen(false); handleLogout(); }} className="btn-outline text-sm">Salir</button>
                    )}
                </div>
            )}
        </nav>
    );
}


function CreditsSummary({ summary, loading }) {
    if (loading) {
        return (
            <div className="px-4 py-3 text-[11px] text-[var(--gf-text-muted)] border-b border-[var(--gf-border)]" data-testid="credits-summary-loading">
                Cargando resumen…
            </div>
        );
    }
    if (!summary) return null;
    const { purchased, consumed, balance, plan, alerts = [] } = summary;

    const rows = [
        // COMPRADO
        {
            group: "comprado",
            label: "Total comprado",
            value: formatEUR(purchased?.total_eur || 0),
            sub: purchased?.transactions
                ? `${purchased.transactions} pago${purchased.transactions !== 1 ? "s" : ""} · Base ${formatEUR(purchased?.total_eur_base || 0)} · IVA ${formatEUR(purchased?.total_iva || 0)}`
                : "Sin compras registradas",
        },
    ];

    return (
        <div className="border-b border-[var(--gf-border)]" data-testid="credits-summary">
            {/* Alertas de saldo bajo */}
            {alerts.length > 0 && alerts.map((a) => (
                <Link
                    key={a.credit_kind}
                    to="/pagos"
                    className={`block px-4 py-2.5 border-l-4 transition-colors ${
                        a.severity === "critical"
                            ? "bg-red-50 border-l-red-500 hover:bg-red-100"
                            : "bg-amber-50 border-l-amber-500 hover:bg-amber-100"
                    }`}
                    data-testid={`low-balance-alert-${a.credit_kind}`}
                >
                    <div className={`text-[10px] uppercase tracking-widest font-semibold mb-0.5 ${a.severity === "critical" ? "text-red-700" : "text-amber-700"}`}>
                        ⚠ Créditos bajos
                    </div>
                    <div className={`text-xs leading-tight ${a.severity === "critical" ? "text-red-800" : "text-amber-800"}`}>
                        Quedan <strong>{formatEUR(a.current)}</strong>. {a.message && <span className="block mt-0.5 opacity-90">{a.message}</span>}
                        <span className="underline underline-offset-2 font-semibold"> Recargar →</span>
                    </div>
                </Link>
            ))}
            {/* Comprado */}
            <div className="px-4 py-3 bg-[var(--gf-subtle)]">
                <div className="flex items-baseline justify-between mb-0.5">
                    <span className="text-[10px] uppercase tracking-widest text-[var(--gf-text-muted)]">Total comprado</span>
                    <span className="mono text-sm font-bold text-[var(--gf-navy)]" data-testid="summary-total-purchased">
                        {formatEUR(purchased?.total_eur || 0)}
                    </span>
                </div>
                <div className="text-[10px] text-[var(--gf-text-muted)]">
                    {purchased?.transactions
                        ? <>Base {formatEUR(purchased?.total_eur_base || 0)} + IVA {formatEUR(purchased?.total_iva || 0)} · {purchased.transactions} pago{purchased.transactions !== 1 ? "s" : ""}</>
                        : <>Sin compras registradas</>}
                </div>
                {purchased?.by_product?.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                        {purchased.by_product.map((p) => (
                            <div key={p.plan_id} className="flex items-baseline justify-between text-[11px]">
                                <span className="text-[var(--gf-text-secondary)] truncate pr-2">
                                    {p.label || p.plan_id}
                                    {p.units > 1 && <span className="text-[var(--gf-text-muted)]"> ×{p.units}</span>}
                                </span>
                                <span className="mono text-[var(--gf-navy)]">{formatEUR(p.eur)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Consumo desglosado */}
            <div className="px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-[var(--gf-text-muted)] mb-1.5">Consumo</div>
                <div className="space-y-0.5" data-testid="summary-consumed">
                    <ConsumeRow label="Informes de balance" value={consumed?.analyses || 0} />
                    <ConsumeRow label="Auditorías bancarias" value={consumed?.bank_audits || 0} />
                    {consumed?.asesor_uploads > 0 && (
                        <ConsumeRow label={`Cargas OCR (${consumed.asesor_facturas || 0} facturas)`} value={consumed.asesor_uploads} />
                    )}
                </div>
            </div>

            {/* Saldo actual: créditos unificados */}
            <div className="px-4 py-3 bg-[var(--gf-navy)] text-white">
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1.5">Créditos disponibles</div>
                <div className="space-y-0.5" data-testid="summary-balance">
                    <div className="flex items-baseline justify-between text-sm">
                        <span className="text-white/80">Saldo</span>
                        <span className="mono text-[var(--gf-gold)] font-semibold text-base">
                            {formatEUR(balance?.credits_eur || 0)}
                        </span>
                    </div>
                </div>
                <Link
                    to="/pagos"
                    className="mt-2 block text-center text-[10px] uppercase tracking-widest text-white/70 hover:text-[var(--gf-gold)] transition-colors"
                    data-testid="navbar-recharge-link"
                >
                    Recargar créditos →
                </Link>
                {Number(balance?.credits_eur || 0) < 24 && (
                    <QuickRechargeButton
                        amount={Number(balance?.credits_eur || 0) < 14 ? 14 : 24}
                        nextPath={window.location.pathname}
                        className="mt-2 w-full bg-[var(--gf-gold)] hover:bg-[#b39568] text-white text-[11px] font-semibold py-2 px-3 inline-flex items-center justify-center gap-1.5 transition-colors"
                        testId="navbar-quick-recharge"
                    />
                )}
            </div>
        </div>
    );
}

function ConsumeRow({ label, value }) {
    return (
        <div className="flex items-baseline justify-between text-[11px]">
            <span className="text-[var(--gf-text-secondary)]">{label}</span>
            <span className="mono text-[var(--gf-navy)]">{value}</span>
        </div>
    );
}
